'use client'

import { BreadCrumbNavBar } from '@/components/navigation/breadcrumb-navbar'
import Link from 'next/link'
import { isNullOrEmpty } from '@/utils/string-extensions'
import { routeCalculatorRelative } from '@/lib/route-calculator'
import { type SVGProps } from 'react'
import { LightPrimaryButton } from '@/components/library/button'
import { toast, Zoom } from 'react-toastify'

const errorCodes = {
  D0001: {
    // eslint-disable-next-line sonarjs/no-duplicate-string
    errorTitle: 'Oops, there was an issue with your order',
  },
  D0002: {
    errorTitle: 'Oops, there was an issue with your order',
  },
  M0001: { errorTitle: 'Oops, there was an issue with your order' },
  M0002: { errorTitle: 'Oops, there was an issue with your order' },
  L0001: { errorTitle: 'Oops, there was an issue with your order' },
  L0002: { errorTitle: 'Oops, there was an issue with your order' },
  G001: {
    errorTitle: 'Oops, there was an issue with your order',
  },
}

export type OrderErrors = keyof typeof errorCodes

export const errorCodeMap = (
  orderMessage: string | null | undefined,
  paid: boolean | null | undefined,
) => {
  switch (orderMessage) {
    case 'Legacy Error':
      return paid ? 'L0002' : 'L0001'
    case 'Duplicate Order Error':
      return paid ? 'D0002' : 'D0001'
    case 'Marketplacer Error':
      return paid ? 'M0002' : 'M0001'
    default:
      return 'G001'
  }
}

const TriangleAlertIcon = (props: SVGProps<SVGSVGElement>) => (
  <svg
    {...props}
    xmlns='http://www.w3.org/2000/svg'
    width='24'
    height='24'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <path d='m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3' />
    <path d='M12 9v4' />
    <path d='M12 17h.01' />
  </svg>
)

const CopyIcon = (props: SVGProps<SVGSVGElement>) => (
  <svg
    {...props}
    xmlns='http://www.w3.org/2000/svg'
    width='24'
    height='24'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <rect width='14' height='14' x='8' y='8' rx='2' ry='2' />
    <path d='M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2' />
  </svg>
)

export const OrderErrorPage = ({ slugs }: { slugs: [OrderErrors, string] }) => {
  const copyLink = () => {
    if (typeof navigator !== 'undefined') {
      navigator.clipboard.writeText(slugs[1])
      toast(<div>Copied</div>, {
        className: 'bg-success text-white',
        position: 'top-center',
        autoClose: 2000,
        transition: Zoom,
      })
    }
  }
  return (
    <div>
      <BreadCrumbNavBar
        breadcrumbs={[
          {
            label: 'Order Error',
          },
        ]}
      />
      <div className='relative flex flex-col items-center'>
        <div className='bg-background flex flex-col items-center justify-center px-4 py-12 sm:px-6 lg:px-8'>
          <div className='mx-auto max-w-md text-center'>
            <TriangleAlertIcon className='mx-auto size-12 text-primary' />
            <h1 className='text-foreground mt-4 text-3xl font-bold tracking-tight sm:text-4xl'>
              Oops, there was an issue with your order
            </h1>
            <p className='text-muted-foreground mt-4'>
              We're sorry, but there seems to be a problem with your recent
              order. Please try again or contact our support team for
              assistance.
            </p>
            {!isNullOrEmpty(slugs[1]) && (
              <div className='text-muted-foreground mt-4 flex items-center justify-center text-sm font-medium'>
                <div className='flex items-center space-x-2 rounded-md bg-gray-200 px-3 py-2 '>
                  <span>Reference #{slugs[1]}</span>
                  <button
                    className='size-5 hover:bg-gray-400'
                    onClick={copyLink}
                  >
                    <CopyIcon className='size-4' />
                    <span className='sr-only'>Copy reference number</span>
                  </button>
                </div>
              </div>
            )}
            <LightPrimaryButton size='large' className='mt-6'>
              <Link
                className='mx-5'
                href={routeCalculatorRelative.support}
                prefetch={false}
              >
                Contact Support
              </Link>
            </LightPrimaryButton>
          </div>
        </div>
      </div>
    </div>
  )
}
